import { useAlertInstances } from "../../api/queries";
import type { AlertInstance, AlertRule } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { AlertInstanceStatePill } from "../../components/StatePill";
import { Modal } from "../../components/Modal";
import { QueryStates } from "../../components/QueryStates";
import "./alerts.css";

interface AlertInstancesModalProps {
  /** The rule whose activity is being viewed, or null when the dialog is
   * closed — same "drive off the target" shape as the edit/delete dialogs. */
  rule: AlertRule | null;
  onClose: () => void;
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString();
}

const COLUMNS: DataTableColumn<AlertInstance>[] = [
  {
    key: "state",
    header: "State",
    cell: (i) => <AlertInstanceStatePill state={i.state} />,
  },
  {
    key: "resource",
    header: "Resource",
    cell: (i) => (i.resourceId ? <span className="data-table__mono">{i.resourceId}</span> : "—"),
  },
  { key: "firstSeen", header: "First seen", cell: (i) => formatTimestamp(i.firstSeenAt) },
  { key: "lastSeen", header: "Last seen", cell: (i) => formatTimestamp(i.lastSeenAt) },
  {
    key: "resolved",
    header: "Resolved",
    cell: (i) => (i.resolvedAt ? formatTimestamp(i.resolvedAt) : "—"),
  },
];

/**
 * A rule's firing/resolved occurrences (ADR-0030 P4-5). Fetched lazily —
 * only while this dialog is actually open (`enabled` below) — rather than
 * eagerly per row on the rules list, so viewing the rules table never fans
 * out one request per rule (P-6).
 */
export function AlertInstancesModal({ rule, onClose }: AlertInstancesModalProps) {
  const { data, isPending, isError, error, refetch } = useAlertInstances(
    rule?.id,
    { limit: 100 },
    { enabled: rule !== null },
  );

  return (
    <Modal open={rule !== null} title={rule ? `Activity — "${rule.name}"` : "Activity"} onClose={onClose}>
      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading activity…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error
            ? error.message
            : "Couldn't load this rule's activity. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No activity yet"
            description="Nothing has triggered this rule's condition since it was created."
          />
        }
      >
        {data && (
          <DataTable columns={COLUMNS} rows={data.items} rowKey={(instance) => instance.id} />
        )}
      </QueryStates>
    </Modal>
  );
}
